import { useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Bookmark, Check, Loader2, Plus, X } from "lucide-react";

export const PRESET_KEYS = ["line2_color", "text_shadow", "overlay", "zoom", "mobile_scale"];
const same = (a, b) => PRESET_KEYS.every((k) => String(a[k] ?? "") === String(b[k] ?? ""));

// Shared, named look presets (colour, shadow, overlay, zoom, phone text size) that apply in one tap.
export function LookPresets({ slide, set }) {
  const [items, setItems] = useState([]);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/hero-look-presets").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const norm = (s) => ({ line2_color: s.line2_color || "", text_shadow: s.text_shadow || "off", overlay: s.overlay ?? -1, zoom: !!s.zoom, mobile_scale: s.mobile_scale || 100 });
  const apply = (p) => { PRESET_KEYS.forEach((k) => set(k, p[k])); toast.success(`Applied “${p.name}”`); };
  const save = async () => {
    if (!name.trim()) return toast.error("Give the preset a name");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/hero-look-presets", { name, ...norm(slide) });
      setItems((x) => [data, ...x]); setNaming(false); setName("");
      toast.success("Look saved as a preset");
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete the preset “${p.name}”?`)) return;
    try { await api.delete(`/admin/hero-look-presets/${p.id}`); setItems((x) => x.filter((i) => i.id !== p.id)); }
    catch (e) { toast.error(errText(e)); }
  };

  const cur = norm(slide);
  return (
    <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-white p-3" data-testid="hero-look-presets">
      <div className="flex items-center justify-between gap-2">
        <Label className="flex items-center gap-1.5"><Bookmark size={14} className="text-[#F5551E]" /> Look presets <span className="text-slate-400 font-normal">(shared with all admins)</span></Label>
        {!naming && <button type="button" onClick={() => setNaming(true)} data-testid="hero-preset-new" className="text-xs font-semibold text-[#F5551E] hover:underline flex items-center gap-1"><Plus size={13} /> Save this look</button>}
      </div>
      {naming && (
        <div className="mt-2 flex items-center gap-2">
          <Input autoFocus value={name} maxLength={40} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setNaming(false); }}
            placeholder="e.g. Bright photo, gold text" data-testid="hero-preset-name" className="h-8 rounded-lg text-xs" />
          <button type="button" onClick={save} disabled={busy} data-testid="hero-preset-save" className="h-8 px-3 rounded-lg bg-[#F5551E] text-white text-xs font-semibold flex items-center gap-1">{busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save</button>
          <button type="button" onClick={() => setNaming(false)} aria-label="Cancel" className="text-slate-400 hover:text-slate-700"><X size={15} /></button>
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {items.length === 0 && <p className="text-[11px] text-slate-400">No presets yet. Set up a look below, then save it here to reuse it on any slide.</p>}
        {items.map((p) => (
          <span key={p.id} className={`group inline-flex items-center rounded-full border text-xs font-semibold transition-colors ${same(cur, p) ? "border-[#F5551E] bg-[#FFF4F0] text-[#F5551E]" : "border-slate-200 text-[#12234A] hover:border-[#F5551E]"}`} data-testid={`hero-preset-${p.id}`}>
            <button type="button" onClick={() => apply(p)} title={`Apply · by ${p.created_by}`} data-testid={`hero-preset-apply-${p.id}`} className="pl-2 pr-1.5 h-7 flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-full border border-slate-300" style={{ background: p.line2_color || "#F5551E" }} /> {p.name}
            </button>
            <button type="button" onClick={() => remove(p)} aria-label={`Delete ${p.name}`} data-testid={`hero-preset-delete-${p.id}`} className="pr-2 h-7 text-slate-300 hover:text-red-600"><X size={12} /></button>
          </span>
        ))}
      </div>
    </div>
  );
}
